import { useState } from 'react';
import { RefreshCw, AlertTriangle, ShieldCheck, Info } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useApprovals, useDecideApproval, useWithdrawApproval } from '../hooks/useAdminOps';
import { type ApprovalDecision, type PendingActionItem } from '../api/adminOps';
import {
  QueueTable,
  StatusChip,
  DetailDrawer,
  ReasonActionBar,
  type ChipTone,
  type QueueColumn,
} from '../components/ui';
import { useAuth } from '../components/AuthProvider';
import ApprovalRequestQueue from '../components/ApprovalRequestQueue';
import {
  AdminGroup,
  AuditRetentionOperation,
  approverSeparationBlocker,
  requiresAdminDecision,
} from './auditRetention.logic';
import { USER_DELETE_OPERATION, userDeletePayloadSummary } from './userDeletion.logic';

const STATUS_FILTERS = [
  'pending',
  'approved',
  'rejected',
  'failed',
  'expired',
  'withdrawn',
] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number] | 'all';

const STATUS_TONES: Record<string, { label: string; tone: ChipTone }> = {
  pending: { label: 'Pending', tone: 'warn' },
  approved: { label: 'Approved', tone: 'ok' },
  rejected: { label: 'Rejected', tone: 'neutral' },
  failed: { label: 'Failed', tone: 'crit' },
  expired: { label: 'Expired', tone: 'neutral' },
  withdrawn: { label: 'Withdrawn', tone: 'neutral' },
};

const OPERATION_CONFIG: Record<string, { label: string; tone: ChipTone }> = {
  'legal_hold.place': { label: 'Place legal hold', tone: 'info' },
  'user.suspend': { label: 'Suspend user', tone: 'crit' },
  'outbox.replay_all': { label: 'Replay all outbox', tone: 'warn' },
  // DM-6208. Without this entry the row still works, but renders unlabelled --
  // a second operator would be asked to approve an unnamed operation.
  'broadcast.send': { label: 'Send broadcast', tone: 'crit' },
  // DM-7185: the most destructive thing the console can do to a donor.
  [USER_DELETE_OPERATION]: { label: 'Delete account', tone: 'crit' },
  [AuditRetentionOperation.PLACE_LEGAL_HOLD]: { label: 'Place audit legal hold', tone: 'info' },
  [AuditRetentionOperation.EXTEND_LEGAL_HOLD]: { label: 'Extend audit legal hold', tone: 'info' },
  [AuditRetentionOperation.RELEASE_LEGAL_HOLD]: {
    label: 'Release audit legal hold',
    tone: 'warn',
  },
  [AuditRetentionOperation.APPROVE_PRUNE]: { label: 'Approve audit partition prune', tone: 'crit' },
};

const REJECT_REASON = { value: 'rejected', label: 'Reject — do not execute' };
const DECISION_REASONS = [
  { value: 'approved', label: 'Approve — execute this action now' },
  REJECT_REASON,
];
const REJECT_ONLY_REASONS = [REJECT_REASON];

function statusChip(status: string) {
  const config = STATUS_TONES[status] ?? { label: status, tone: 'neutral' as ChipTone };
  return <StatusChip label={config.label} tone={config.tone} />;
}

function operationChip(operation: string) {
  const config = OPERATION_CONFIG[operation] ?? { label: operation, tone: 'neutral' as ChipTone };
  return <StatusChip label={config.label} tone={config.tone} />;
}

function expiryChip(row: PendingActionItem) {
  if (row.status !== 'pending') {
    return <span className="text-gray-text">—</span>;
  }
  const msLeft = new Date(row.expires_at).getTime() - Date.now();
  if (msLeft <= 0) {
    return <StatusChip label="Expired" tone="crit" />;
  }
  const hoursLeft = msLeft / 3_600_000;
  const tone: ChipTone = hoursLeft < 6 ? 'warn' : 'neutral';
  const label =
    hoursLeft < 1
      ? `${Math.max(1, Math.floor(msLeft / 60_000))}m left`
      : hoursLeft < 48
        ? `${Math.floor(hoursLeft)}h left`
        : `${Math.floor(hoursLeft / 24)}d left`;
  return <StatusChip label={label} tone={tone} />;
}

/** The checker must see exactly what will execute: flatten the payload to rows. */
function payloadEntries(payload: unknown): { key: string; value: string }[] {
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) {
    return [{ key: 'payload', value: JSON.stringify(payload, null, 2) ?? 'null' }];
  }
  return Object.entries(payload as Record<string, unknown>).map(([key, value]) => ({
    key,
    value: typeof value === 'string' ? value : (JSON.stringify(value, null, 2) ?? 'null'),
  }));
}

/**
 * DM-7185: a deletion is summarised in words rather than dumped as raw keys --
 * the approver has to see whose account, why, and (for a terms violation) that
 * approving also suspends it. Null for every other operation, and for a
 * user.delete payload the summary does not recognise, so those keep the raw
 * dump and the approver is never shown less than what will execute.
 */
function deletionSummary(row: PendingActionItem) {
  return row.operation === USER_DELETE_OPERATION ? userDeletePayloadSummary(row.payload) : null;
}

export default function Approvals() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { user, hasRole } = useAuth();
  const canDecide = hasRole('admin', 'admin-operator');
  const queue = useApprovals(statusFilter === 'all' ? {} : { status: statusFilter });
  const decideMutation = useDecideApproval();
  const withdrawMutation = useWithdrawApproval();

  const visibleRows = queue.rows;

  const selected = queue.rows.find(row => row.id === selectedId) ?? null;
  const isOwnRequest = selected !== null && user !== null && selected.requested_by === user.sub;
  const selectedDeletion = selected === null ? null : deletionSummary(selected);
  const selectedNeedsAdmin = selected !== null && requiresAdminDecision(selected.operation);
  const canDecideSelected = selectedNeedsAdmin ? hasRole(AdminGroup.ADMIN) : canDecide;
  const separationBlocker =
    selected === null
      ? null
      : approverSeparationBlocker(selected.operation, selected.payload, user?.sub);

  const closeDrawer = () => setSelectedId(null);

  const submitDecision = (decision: string, note: string | undefined) => {
    if (!selected) return;
    decideMutation.mutate(
      { id: selected.id, decision: decision as ApprovalDecision, note },
      { onSuccess: closeDrawer }
    );
  };

  const withdraw = () => {
    if (!selected) return;
    withdrawMutation.mutate({ id: selected.id }, { onSuccess: closeDrawer });
  };

  const columns: QueueColumn<PendingActionItem>[] = [
    {
      key: 'requested_at',
      header: 'Requested',
      render: row => (
        <span className="text-gray-text whitespace-nowrap">
          {formatDistanceToNow(new Date(row.requested_at), { addSuffix: true })}
        </span>
      ),
    },
    { key: 'operation', header: 'Operation', render: row => operationChip(row.operation) },
    {
      key: 'requested_by',
      header: 'Requested by',
      render: row => (
        <span className="text-gray-text font-mono text-xs break-all">{row.requested_by}</span>
      ),
    },
    { key: 'status', header: 'Status', render: row => statusChip(row.status) },
    { key: 'expires', header: 'Expires', render: row => expiryChip(row) },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <ShieldCheck className="w-7 h-7 text-primary" />
            Approvals
          </h1>
          <p className="text-gray-text">
            Dual-control queue: privileged actions execute only after a second admin approves.
          </p>
        </div>
        <button
          onClick={() => queue.refetch()}
          className="btn-secondary flex items-center space-x-2"
          disabled={queue.isFetching}
        >
          <RefreshCw className={`w-4 h-4 ${queue.isFetching ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Self-approval callout */}
      <div className="glass-card p-4 flex items-start gap-3 border border-primary/20">
        <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
        <p className="text-sm text-gray-text">
          You cannot approve your own requests — the server rejects self-approval. Ask another admin
          to review, or withdraw a request you no longer need.
        </p>
      </div>

      {/* Status filter chips */}
      <div className="glass-card p-4 flex items-center gap-2 flex-wrap">
        <span className="text-sm text-gray-text mr-2">Status:</span>
        {[...STATUS_FILTERS, 'all' as const].map(value => (
          <button
            key={value}
            onClick={() => {
              setStatusFilter(value);
              setSelectedId(null);
            }}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors capitalize ${
              statusFilter === value
                ? 'bg-primary/20 text-primary'
                : 'bg-gray-500/10 text-gray-text hover:text-white'
            }`}
          >
            {value}
          </button>
        ))}
      </div>

      {/* Pending actions (admin operations console) */}
      <h2 className="text-xl font-semibold text-white">Operations console</h2>
      {queue.isError ? (
        <div className="glass-card p-6 border border-red-500/30 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-white font-medium mb-1">Failed to load approvals</p>
            <p className="text-sm text-gray-text mb-3">
              {queue.error instanceof Error ? queue.error.message : 'Unknown error'}
            </p>
            <button onClick={() => queue.refetch()} className="btn-secondary">
              Retry
            </button>
          </div>
        </div>
      ) : (
        <QueueTable
          columns={columns}
          rows={visibleRows}
          rowKey={row => row.id}
          isLoading={queue.isPending}
          onRowClick={row => setSelectedId(row.id)}
          emptyTitle="No pending actions"
          emptyHint={
            statusFilter === 'all'
              ? 'Nothing is waiting for dual-control review.'
              : `No actions with status "${statusFilter}".`
          }
          hasNextPage={queue.hasNextPage}
          isFetchingNextPage={queue.isFetchingNextPage}
          onLoadMore={() => queue.fetchNextPage()}
        />
      )}

      {/* DM-6437: content, experiment and CDN approval requests share this inbox. */}
      <ApprovalRequestQueue />

      {/* Detail drawer */}
      <DetailDrawer
        open={selected !== null}
        title="Pending action"
        onClose={closeDrawer}
        footer={
          selected &&
          (selected.status === 'pending' ? (
            <div className="space-y-3">
              {isOwnRequest ? (
                <>
                  <p className="text-sm text-gray-text">
                    You requested this action, so you cannot decide it. You can withdraw it if it is
                    no longer needed.
                  </p>
                  <button
                    onClick={withdraw}
                    disabled={withdrawMutation.isPending}
                    className="w-full px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white font-medium transition-colors disabled:opacity-50"
                  >
                    {withdrawMutation.isPending ? 'Withdrawing…' : 'Withdraw request'}
                  </button>
                </>
              ) : canDecideSelected ? (
                <>
                  {separationBlocker && (
                    <p className="text-sm text-gray-text">{separationBlocker}</p>
                  )}
                  <ReasonActionBar
                    key={separationBlocker === null ? 'decide' : 'reject-only'}
                    reasons={separationBlocker === null ? DECISION_REASONS : REJECT_ONLY_REASONS}
                    actionLabel="Submit decision"
                    isPending={decideMutation.isPending}
                    notePlaceholder="Optional decision note for the audit trail"
                    onSubmit={submitDecision}
                  />
                </>
              ) : selectedNeedsAdmin ? (
                <p className="text-sm text-gray-text">
                  Audit retention actions can only be decided by another user with the admin role.
                </p>
              ) : (
                <p className="text-sm text-gray-text">
                  Your role has read-only access — deciding approvals requires the admin or
                  admin-operator role.
                </p>
              )}
            </div>
          ) : null)
        }
      >
        {selected && (
          <>
            <div className="flex items-center gap-2 flex-wrap">
              {operationChip(selected.operation)}
              {statusChip(selected.status)}
              {expiryChip(selected)}
            </div>

            {selected.status === 'failed' && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                <p className="text-sm font-medium text-red-400 mb-1">Execution failed</p>
                <p className="text-sm text-white break-words">
                  {selected.decision_note ?? 'No failure detail recorded.'}
                </p>
                <p className="text-xs text-gray-text mt-2">
                  Failed actions are terminal — re-request the operation from its source page to try
                  again.
                </p>
              </div>
            )}

            <div>
              <p className="text-xs text-gray-text uppercase tracking-wide mb-2">
                What will execute
              </p>
              {selectedDeletion ? (
                <>
                  <div className="rounded-lg border border-gray-border divide-y divide-gray-border/50">
                    {selectedDeletion.rows.map(entry => (
                      <div key={entry.label} className="px-3 py-2 flex gap-3">
                        <span className="text-xs text-gray-text flex-shrink-0 w-36">
                          {entry.label}
                        </span>
                        <span className="text-sm text-white break-all whitespace-pre-wrap">
                          {entry.value}
                        </span>
                      </div>
                    ))}
                  </div>
                  {selectedDeletion.consequence && (
                    <p
                      className={`mt-2 text-sm ${
                        selectedDeletion.consequence.tone === 'crit'
                          ? 'p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300'
                          : 'text-gray-text'
                      }`}
                    >
                      {selectedDeletion.consequence.text}
                    </p>
                  )}
                </>
              ) : (
                <div className="rounded-lg border border-gray-border divide-y divide-gray-border/50">
                  {payloadEntries(selected.payload).map(entry => (
                    <div key={entry.key} className="px-3 py-2 flex gap-3">
                      <span className="text-xs text-gray-text font-mono flex-shrink-0 w-36 break-all">
                        {entry.key}
                      </span>
                      <span className="text-sm text-white font-mono break-all whitespace-pre-wrap">
                        {entry.value}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-1 text-sm">
              <p className="text-gray-text">
                Requested by{' '}
                <span className="text-white font-mono text-xs break-all">
                  {selected.requested_by}
                </span>{' '}
                {formatDistanceToNow(new Date(selected.requested_at), { addSuffix: true })}
              </p>
              <p className="text-gray-text">
                Expires {new Date(selected.expires_at).toLocaleString()}
              </p>
              {selected.decided_by && (
                <p className="text-gray-text">
                  Decided by{' '}
                  <span className="text-white font-mono text-xs break-all">
                    {selected.decided_by}
                  </span>
                  {selected.decided_at &&
                    ` ${formatDistanceToNow(new Date(selected.decided_at), { addSuffix: true })}`}
                </p>
              )}
              {selected.decision_note && selected.status !== 'failed' && (
                <p className="text-gray-text">
                  Note: <span className="text-white">{selected.decision_note}</span>
                </p>
              )}
            </div>
          </>
        )}
      </DetailDrawer>
    </div>
  );
}
